/**
 * A debounced "latest wins" runner (CL-18307).
 *
 * Two separate defects are fixed by one primitive, because they are two halves
 * of the same mistake -- treating every keystroke as an independent, isolated
 * request:
 *
 *   1. NO DEBOUNCE. The Add Company subdomain watcher fired an availability
 *      request per character, so a 63-character subdomain produced 63 probes
 *      (plus the token traffic each unauthenticated one triggered). `schedule`
 *      collapses a burst of calls into a single `run` invocation after the
 *      caller stops for `delay` ms.
 *
 *   2. STALE RESPONSES OVERWRITING FRESH ONES. Even with a debounce, two
 *      probes can be in flight across a typing pause, and nothing guaranteed
 *      they resolved in the order they were issued. A slow probe for "acme"
 *      landing after a fast probe for "acme-uk" would paint "acme"'s verdict
 *      next to the text "acme-uk" -- the user sees a result for a value they
 *      no longer have in the box. `seq` below makes the newest call the only
 *      one that can deliver a result, regardless of completion order.
 *
 * Why the guard lives here and not in the component: three screens
 * (passport_account/CreateCompany.vue, modal/CreateCompanyModal.vue and
 * account/register.vue) run near-identical copies of that watcher and all
 * three funnel through one Vuex action. Guarding in the components would mean
 * three chances to get it wrong, and would leave the Vuex commit itself
 * unguarded -- a stale response would still reach the store and repaint the
 * badge even if the component ignored its own promise.
 *
 * Deliberately dependency-free: this repo has no lodash and no test runner, so
 * a hand-rolled 40 lines that can be exercised by plain node beats adding a
 * dependency to fix a debounce.
 */

/**
 * @typedef {Object} Outcome
 * @property {boolean} [superseded] True when a newer call replaced this one
 *   before it could deliver a result. `result` is then absent -- callers must
 *   check this flag and leave their UI untouched, NOT treat it as a negative
 *   answer.
 * @property {*} [result] The value `run` resolved with. Only ever present on
 *   the newest call.
 * @property {*} payload The payload this call was scheduled with, echoed back
 *   so a caller can confirm the result it is about to render belongs to the
 *   value currently on screen.
 */

/**
 * @param {Object} options
 * @param {function(*, {onCancel: function(function(): void): void}): Promise} options.run
 *   Performs the real work. Receives the payload and a registration hook: call
 *   `onCancel(fn)` to have `fn` invoked if this call gets superseded, which is
 *   where a transport can abort an in-flight request it no longer needs.
 * @param {number} [options.delay=400] Quiet period in ms before `run` fires.
 *   400ms is comfortably longer than a fast typist's inter-key gap (~120ms)
 *   and short enough to feel immediate once typing stops.
 * @returns {{schedule: function(*): Promise<Outcome>, cancel: function(): void, isPending: function(): boolean}}
 */
export function createDebouncedLatest({ run, delay = 400 }) {
  if (typeof run !== "function") {
    throw new TypeError("createDebouncedLatest requires a `run` function");
  }

  let timer = null;

  // Monotonic. Only the call whose seq still equals `seq` when it resolves is
  // allowed to deliver a result.
  let seq = 0;

  // Settles whatever call is currently outstanding as superseded, from
  // whichever stage it is in -- still waiting out its quiet period, or already
  // on the wire. Both stages matter: a call superseded while still in its
  // debounce window has had its timer cleared, so nothing else would ever
  // settle its promise and a caller awaiting it would hang forever.
  let abandonOutstanding = null;

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const supersedeOutstanding = () => {
    const abandon = abandonOutstanding;
    abandonOutstanding = null;
    clearTimer();
    if (typeof abandon === "function") {
      abandon();
    }
  };

  return {
    /**
     * Queue `run(payload)` to fire once the caller stops scheduling for
     * `delay` ms. Every earlier pending or in-flight call is superseded.
     *
     * Always resolves for the superseded case, never rejects: a superseded
     * call is not an error, it is a call whose answer stopped being wanted.
     * Rejecting would make every keystroke but the last produce an unhandled
     * rejection in the console -- the very noise this ticket is about.
     */
    schedule(payload) {
      supersedeOutstanding();

      const mine = ++seq;

      return new Promise((resolve, reject) => {
        let settled = false;

        const settleSuperseded = () => {
          if (!settled) {
            settled = true;
            resolve({ superseded: true, payload });
          }
        };

        // Registered by `run` if its transport can be aborted.
        let transportCancel = null;

        abandonOutstanding = () => {
          if (typeof transportCancel === "function") {
            try {
              transportCancel();
            } catch (e) {
              // A transport that throws while being cancelled must not take
              // the newer request down with it.
            }
          }
          settleSuperseded();
        };

        timer = setTimeout(() => {
          timer = null;

          if (settled || mine !== seq) {
            settleSuperseded();
            return;
          }

          const onCancel = (fn) => {
            transportCancel = fn;
          };

          Promise.resolve()
            .then(() => run(payload, { onCancel }))
            .then((result) => {
              if (settled || mine !== seq) {
                settleSuperseded();
                return;
              }
              settled = true;
              resolve({ result, payload });
            })
            .catch((error) => {
              // A failure nobody is waiting for any more is discarded the same
              // way a stale success is. This is the other half of the
              // console-noise fix: superseded probes that 401'd used to surface
              // as errors against a value the user had already changed.
              if (settled || mine !== seq) {
                settleSuperseded();
                return;
              }
              settled = true;
              reject(error);
            });
        }, delay);
      });
    },

    /**
     * Drop anything pending or in flight. Nothing further will be delivered,
     * and the outstanding promise settles as superseded rather than hanging.
     * Used when the field is cleared or edited into an uncheckable value.
     */
    cancel() {
      seq++;
      supersedeOutstanding();
    },

    /** True while a scheduled call is waiting out its quiet period. */
    isPending() {
      return timer !== null;
    },
  };
}

export default createDebouncedLatest;
